import { useCallback } from 'react';
import type { FC } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useIntersectionObserver } from 'usehooks-ts';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { isNotAvailableStatus } from '../../../bff-api/utils/store-utils';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { useAddProductToBag } from '../../../hooks/useAddProductToBag';
import { Button } from '../../../ui-components';
import { isGiftCard, isOutOfStock } from '../../../utils/product-utils';
import type { Product } from '../../../bff-api/products/model';

export interface AddToBagButtonProps {
    trackIfInViewport?: boolean;
    headerHeight?: number;
    includePrice?: boolean;
    customProduct?: Product;
    ignoreInventory?: boolean;
    onAddToBag?: () => Promise<boolean>;
}

export const AddToBagButton: FC<AddToBagButtonProps> = ({
    trackIfInViewport,
    headerHeight = 0,
    includePrice,
    customProduct,
    ignoreInventory,
    onAddToBag,
}) => {
    const { data: basket } = useBasket();
    const {
        product: usePdpProduct,
        inventoryStore: {
            currentInventory,
            flags: { hasEnoughStock },
        },
        isLoadingCurrentInventory,
        selectedQuantity,
        selectedShippingOptionType,
        setShouldShowStickyBuyBoxAddToBagButton,
    } = usePDP();
    const product = customProduct || usePdpProduct;

    const addProductToBag = useAddProductToBag({ openDrawer: true, ignoreInventory });

    const onChangeIntersection = useCallback(
        (isIntersecting: boolean) => {
            if (trackIfInViewport) {
                setShouldShowStickyBuyBoxAddToBagButton(!isIntersecting);
            }
        },
        [setShouldShowStickyBuyBoxAddToBagButton, trackIfInViewport],
    );

    const { ref: addToBagButtonRef } = useIntersectionObserver({
        onChange: onChangeIntersection,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: [0.25, 0.75],
    });

    if (!product) {
        return null;
    }

    const isGiftCardProduct = isGiftCard(product);
    const outOfStock = isGiftCardProduct ? false : isOutOfStock(product);
    const isNotAvailable = isGiftCardProduct
        ? false
        : isNotAvailableStatus(currentInventory.status);

    const onAddProductToBag = async (): Promise<void> => {
        if (onAddToBag) {
            const shouldContinue = await onAddToBag();
            if (!shouldContinue) {
                return;
            }
        }

        addProductToBag.mutate({
            source: 'PDP',
            product,
            quantity: selectedQuantity,
            shippingOptionType: selectedShippingOptionType,
            applyExtend: true,
            store: currentInventory.store,
        });
    };

    const isAddToBagDisabled =
        !basket ||
        outOfStock ||
        (!addProductToBag.isLoading &&
            ((!hasEnoughStock && !isGiftCardProduct) || isLoadingCurrentInventory));
    const isAddToBagLoading = addProductToBag.isLoading || isLoadingCurrentInventory;

    return (
        <Button
            ref={addToBagButtonRef}
            isDisabled={isAddToBagDisabled}
            isLoading={isAddToBagLoading}
            id="buy-box-add-to-bag"
            onClick={onAddProductToBag}
            wide
        >
            {outOfStock ? (
                <FormattedMessage id="buy.box.out.of.stock" defaultMessage="Out of stock" />
            ) : null}

            {!outOfStock && isNotAvailable ? (
                <FormattedMessage id="buy.box.not.available" defaultMessage="Not available" />
            ) : null}

            {!outOfStock && !isNotAvailable ? (
                <>
                    <FormattedMessage id="buy.box.add.to.bag" defaultMessage="Add to bag" />
                    {includePrice ? (
                        <>
                            {' - '}
                            <FormattedNumber
                                style="currency"
                                currency={product.currency}
                                value={product.price ?? 0}
                            />
                        </>
                    ) : null}
                </>
            ) : null}
        </Button>
    );
};

AddToBagButton.propTypes = {
    trackIfInViewport: PropTypes.bool,
    headerHeight: PropTypes.number,
    includePrice: PropTypes.bool,
};

AddToBagButton.defaultProps = {
    trackIfInViewport: false,
    headerHeight: 0,
    includePrice: false,
    customProduct: undefined,
    ignoreInventory: false,
    onAddToBag: undefined,
};
